/**
 * Phase 32 — a country's alert channels: the read (targets masked) and
 * the three writes — save one channel, remove it, send a test message.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DropGenNotificationRow, DropGenNotificationSendResult, DropGenNotificationUpdate } from '@trafficpulse/shared';

import { api } from '@/services/api';

import { dropGeneratorKeys } from '../../../useDropGenerator';

interface Envelope<T> {
  data: T;
}

const BASE = '/drop-generator/notifications';

export const notificationKeys = (countryKey: string) => [...dropGeneratorKeys.all, 'notifications', countryKey] as const;

export function useNotifications(countryKey: string, enabled: boolean) {
  return useQuery({
    queryKey: notificationKeys(countryKey),
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenNotificationRow[]>>(`${BASE}/${countryKey}`);
      return res.data.data;
    },
    enabled,
    staleTime: 10_000,
  });
}

export function useNotificationMutations(countryKey: string) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: notificationKeys(countryKey) });
  };
  const save = useMutation({
    mutationFn: async (input: { channel: string; body: DropGenNotificationUpdate }) => {
      const res = await api.put<Envelope<DropGenNotificationRow>>(`${BASE}/${countryKey}/${input.channel}`, input.body);
      return res.data.data;
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (channel: string) => {
      await api.delete(`${BASE}/${countryKey}/${channel}`);
    },
    onSuccess: invalidate,
  });
  const test = useMutation({
    mutationFn: async (channel: string) => {
      const res = await api.post<Envelope<DropGenNotificationSendResult>>(`${BASE}/${countryKey}/${channel}/test`);
      return res.data.data;
    },
    onSuccess: invalidate,
  });
  return { save, remove, test };
}
